import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { WipeLink } from './Wipe';
import { CHAPTERS } from '../data/site';
import { MaskedLines, FadeUp } from './Reveal';
import Canopy from './Canopy';
import Footer from './Footer';

const ChapterShell = ({ chapter, children, heroImg, heroCaption, nextOverride, heroContain = false }) => {
  const idx = CHAPTERS.findIndex((c) => c.slug === chapter.slug);
  const next = CHAPTERS[(idx + 1) % CHAPTERS.length];
  const target = nextOverride || { to: `/chapter/${next.slug}`, label: `${next.n}, ${next.title}`, color: next.color, eyebrow: 'Next Chapter' };
  const heroRef = useRef(null);
  const { scrollYProgress: pageProgress } = useScroll();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const numY = useTransform(scrollYProgress, [0, 1], ['0%', '-30%']);

  return (
    <div className="bg-paper" data-testid={`chapter-${chapter.slug}`}>
      <motion.div
        data-testid="reading-progress"
        className="fixed top-0 left-0 right-0 h-[3px] z-[80] origin-left"
        style={{ scaleX: pageProgress, background: chapter.color }}
      />
      <section ref={heroRef} className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden" style={{ background: chapter.color }}>
        <motion.div
          initial={{ y: '-100%' }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="absolute top-14 md:top-16 left-0 right-0 z-20"
        >
          <Canopy a={chapter.canopy} b="#F6EFE3" className="h-[52px] md:h-[68px] w-full opacity-95" />
        </motion.div>
        <motion.div style={{ y: numY }} className="absolute top-16 right-0 md:right-6 text-[42vw] md:text-[30vw] font-light leading-none text-paper/10 select-none pointer-events-none">
          {chapter.n}
        </motion.div>
        <div className="bandhani-dot absolute inset-0 text-paper/[0.07] pointer-events-none" />
        <div className="relative px-5 md:px-10 lg:px-24 pt-40 md:pt-44 pb-10 md:pb-14">
          <FadeUp y={14}>
            <p className="text-paper/70 text-xs md:text-sm font-semibold uppercase tracking-[0.35em]">
              Chapter · {chapter.n}
            </p>
          </FadeUp>
          <MaskedLines
            onLoad
            delay={0.25}
            lines={[chapter.title]}
            className="mt-4 text-paper text-[18vw] md:text-[11vw] font-light tracking-tighter leading-[0.9]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-6 text-paper/80 text-base md:text-lg max-w-xl leading-relaxed"
          >
            {chapter.tag}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-4 text-paper/50 text-[11px] md:text-xs uppercase tracking-[0.3em]"
          >
            {chapter.keys}
          </motion.p>
        </div>
        {heroImg && (
          <div className="relative px-5 md:px-10 lg:px-24 pb-16">
            <motion.div
              initial={{ clipPath: 'inset(100% 0 0 0)' }}
              animate={{ clipPath: 'inset(0% 0 0 0)' }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden"
            >
              {heroContain ? (
                <motion.img
                  src={heroImg}
                  alt={chapter.title}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full max-h-[58vh] object-contain py-6"
                />
              ) : (
                <motion.img
                  src={heroImg}
                  alt={chapter.title}
                  style={{ y: imgY, scale: 1.15 }}
                  className="w-full h-[46vh] md:h-[62vh] object-cover"
                />
              )}
            </motion.div>
            {heroCaption && (
              <p className="mt-3 text-paper/50 text-[11px] uppercase tracking-[0.25em]">{heroCaption}</p>
            )}
          </div>
        )}
      </section>

      {children}

      <WipeLink
        to={target.to}
        data-testid="next-chapter-link"
        className="group relative block overflow-hidden px-5 md:px-10 lg:px-24 py-24 md:py-32"
        style={{ background: target.color }}
      >
        <div className="bandhani-dot absolute inset-0 text-paper/[0.06] pointer-events-none" />
        <p className="relative text-paper/60 text-xs uppercase tracking-[0.35em]">{target.eyebrow}</p>
        <div className="relative mt-4 flex items-end justify-between gap-6">
          <span className="text-paper text-5xl md:text-8xl font-light tracking-tight leading-none transition-transform duration-500 group-hover:translate-x-4">
            {target.label}
          </span>
          <span className="text-paper text-4xl md:text-6xl transition-transform duration-500 group-hover:translate-x-3 group-hover:-translate-y-1">→</span>
        </div>
      </WipeLink>
      <Footer />
    </div>
  );
};

export default ChapterShell;
